import { useEffect, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsLeftRight, X } from "@phosphor-icons/react";
import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";

/**
 * Bulk-transfer selected vehicles to another branch.
 *
 * Renders the list of source branches involved (admin can transfer mixed sources;
 * staff only sees their own branch's stock anyway), the target picker, and a note.
 * Server validates available-only + staff-branch restrictions.
 */
export default function VehicleTransferDialog({
  open,
  vehicles,
  onClose,
  onDone,
}) {
  const { user } = useAuth();
  // Transfer dialog MUST see other branches (staff branch scoping on
  // /branches would collapse the list to just the source itself and
  // produce the "No other active branches of the same type" false alarm).
  const { branches, byId } = useBranches({ activeOnly: true, includeAll: true });
  const confirm = useConfirm();
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget("");
    setNote("");
  }, [open]);

  if (!open) return null;

  const sourceBranchIds = Array.from(
    new Set(vehicles.map((v) => v.branch_id).filter(Boolean))
  );
  // Only same-branch-type transfers are allowed. Cross-type moves (e.g.
  // main dealer → sub-dealer) must go through a sub-dealer INVOICE, not
  // an internal stock transfer. So we filter the target dropdown to the
  // same branch_type_id as the source(s) whenever we can figure it out.
  const sourceTypeIds = Array.from(
    new Set(
      sourceBranchIds
        .map((id) => byId[id]?.branch_type_id)
        .filter(Boolean)
    )
  );
  const singleSourceType = sourceTypeIds.length === 1 ? sourceTypeIds[0] : null;
  const targetOptions = branches.filter((b) => {
    if (!b.active) return false;
    if (sourceBranchIds.length === 1 && b.id === sourceBranchIds[0]) return false;
    // Different-type branches are hidden from the dropdown (backend also
    // rejects them). If we can't determine the source type, fall back to
    // the historical behaviour (show everything) so we don't accidentally
    // hide options in edge cases.
    if (singleSourceType && b.branch_type_id && b.branch_type_id !== singleSourceType) return false;
    return true;
  });

  const blocked = vehicles.filter((v) => v.status !== "available");
  const canSubmit = target && vehicles.length > 0 && blocked.length === 0;

  const handleSubmit = async () => {
    if (!(await confirm({ title: "Initiate inventory transfer?", body: "Vehicle will be moved between branches via the transfer workflow." }))) return;
    if (!canSubmit) return;
    setWorking(true);
    try {
      const { data } = await api.post("/inventory/transfer", {
        vehicle_ids: vehicles.map((v) => v.id),
        to_branch_id: target,
        note: note.trim() || null,
      });
      toast.success(
        `Transfer requested — ${data.pending} vehicle(s) pending approval at ${byId[target]?.name || "branch"}`
      );
      // Auto-open the delivery challan(s) for the freshly created transfer(s)
      // so the operator can print them and hand them off to the driver.
      const created = data.transfers || [];
      for (const tr of created) {
        try {
          const r = await api.get(`/inventory/transfers/${tr.id}/challan`);
          const w = window.open("", "_blank", "width=900,height=1100");
          if (!w) {
            toast.message("Pop-ups blocked — open the Transfer History to print the challan.");
            break;
          }
          // Lazy require to avoid a circular import at module init.
          const { default: buildChallanHtml } = await import(
            "@/components/deliveryChallanHtml"
          );
          w.document.write(buildChallanHtml(r.data));
          w.document.close();
        } catch {
          // Best-effort; user can still print from the history drawer.
        }
      }
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="vehicle-transfer-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-canvas-card border border-canvas-line w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-start justify-between p-5 border-b border-canvas-line">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
              Transfer Vehicles
            </div>
            <h2 className="text-lg font-semibold text-ink">
              {vehicles.length} vehicle{vehicles.length === 1 ? "" : "s"} selected
            </h2>
          </div>
          <button
            data-testid="close-transfer-dialog"
            onClick={onClose}
            className="p-1 text-ink-muted hover:text-ink"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {blocked.length > 0 && (
            <div className="border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              {blocked.length} selected vehicle(s) are not Available and cannot be
              transferred. Deselect them to continue:{" "}
              <span className="font-mono">
                {blocked.slice(0, 3).map((v) => v.vin).join(", ")}
                {blocked.length > 3 ? "…" : ""}
              </span>
            </div>
          )}

          {sourceBranchIds.length > 0 && (
            <div className="text-xs text-ink-muted">
              From:{" "}
              <span className="font-mono text-ink">
                {sourceBranchIds.map((id) => byId[id]?.name || "—").join(", ")}
              </span>
            </div>
          )}

          {/* Full selected-vehicle list so the user can audit before confirming */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                Selected Vehicles ({vehicles.length})
              </label>
              <span className="font-mono text-[10px] text-ink-muted">scroll to review</span>
            </div>
            <div className="border border-canvas-line max-h-56 overflow-y-auto" data-testid="transfer-vehicle-list">
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">VIN</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Model</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Color</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">From</th>
                    <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => {
                    const blockedRow = v.status !== "available";
                    return (
                      <tr
                        key={v.id}
                        className={`border-t border-canvas-line ${blockedRow ? "bg-destructive/5" : ""}`}
                        data-testid={`transfer-vehicle-${v.id}`}
                      >
                        <td className="px-2 py-1 font-mono text-[10px]">{v.vin}</td>
                        <td className="px-2 py-1">{v.package_name || v.model_category || "—"}</td>
                        <td className="px-2 py-1 text-ink-muted">{v.color || "—"}</td>
                        <td className="px-2 py-1 font-mono text-[10px] text-ink-muted">{byId[v.branch_id]?.name || "—"}</td>
                        <td className={`px-2 py-1 font-mono text-[10px] uppercase ${blockedRow ? "text-destructive font-bold" : "text-ink-muted"}`}>
                          {v.status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Target Branch
            </label>
            <select
              data-testid="transfer-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="" disabled>
                Select a branch…
              </option>
              {targetOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {targetOptions.length === 0 && (
              <div className="mt-2 text-[11px] text-[#A35700]" data-testid="transfer-no-options">
                No other active branches of the same type available.
                {singleSourceType && (
                  <> Cross-type transfers (e.g. main dealer → sub-dealer) must be raised as a <b>sub-dealer invoice</b>, not a transfer.</>
                )}
              </div>
            )}
            {user?.role !== "admin" && (
              <div className="mt-2 text-[11px] text-ink-subtle">
                Note: you can only send vehicles out from your branch. Pulling stock
                in must be done by an admin or the source branch&apos;s staff.
              </div>
            )}
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Note (optional)
            </label>
            <input
              data-testid="transfer-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for transfer, e.g., Restocking Bangalore showroom"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>

          <div className="border border-canvas-line bg-canvas-panel p-3 text-[11px] text-ink-muted leading-relaxed">
            <strong className="text-ink">Pending approval workflow:</strong> Vehicles will move to the target branch immediately but stay locked (not sellable) until the destination branch&apos;s manager approves. You can cancel anytime before that.
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-canvas-line flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-transfer"
            onClick={handleSubmit}
            disabled={!canSubmit || working}
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <ArrowsLeftRight size={13} weight="bold" />
            {working ? "Submitting…" : "Submit for approval"}
          </button>
        </footer>
      </div>
    </div>
  );
}
